import { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../App';

export interface OrganizationFeature {
  feature_key: string;
  feature_name: string;
  description: string;
  category: string;
  is_enabled: boolean;
  modified_at: string | null;
  modified_by: string | null;
}

interface OrganizationSettingsResponse {
  features: OrganizationFeature[];
  featuresByCategory: Record<string, OrganizationFeature[]>;
}

interface UseOrganizationSettingsReturn {
  features: OrganizationFeature[];
  featuresByCategory: Record<string, OrganizationFeature[]>;
  loading: boolean;
  error: string | null;
  isFeatureEnabled: (featureKey: string) => boolean;
  updateFeature: (featureKey: string, isEnabled: boolean) => Promise<void>;
  refetch: () => void;
}

export const useOrganizationSettings = (organizationId: string | null): UseOrganizationSettingsReturn => {
  const [features, setFeatures] = useState<OrganizationFeature[]>([]);
  const [featuresByCategory, setFeaturesByCategory] = useState<Record<string, OrganizationFeature[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSettings = useCallback(async () => {
    if (!organizationId) {
      setLoading(false);
      setFeatures([]);
      setFeaturesByCategory({});
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await api.get<{ success: boolean; data: OrganizationSettingsResponse }>(
        `/organizations/${organizationId}/settings`
      );

      if (response.data.success) {
        setFeatures(response.data.data.features);
        setFeaturesByCategory(response.data.data.featuresByCategory);
      } else {
        setError('Failed to fetch organization settings');
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch organization settings';
      setError(errorMessage);
      setFeatures([]);
      setFeaturesByCategory({});
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const isFeatureEnabled = useCallback((featureKey: string): boolean => {
    const feature = features.find(f => f.feature_key === featureKey);
    return feature?.is_enabled ?? true; // Default to enabled if not found
  }, [features]);

  const updateFeature = useCallback(async (featureKey: string, isEnabled: boolean): Promise<void> => {
    if (!organizationId) {
      throw new Error('No organization selected');
    }

    const response = await api.put(
      `/organizations/${organizationId}/settings/${featureKey}`,
      { is_enabled: isEnabled }
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to update setting');
    }

    // Optimistically update local state
    setFeatures(prev => prev.map(f => 
      f.feature_key === featureKey 
        ? { ...f, is_enabled: isEnabled, modified_at: new Date().toISOString() }
        : f
    ));

    // Also update featuresByCategory
    setFeaturesByCategory(prev => {
      const updated = { ...prev };
      for (const category in updated) {
        updated[category] = updated[category].map(f =>
          f.feature_key === featureKey
            ? { ...f, is_enabled: isEnabled, modified_at: new Date().toISOString() }
            : f
        );
      }
      return updated;
    });
  }, [organizationId]);

  return {
    features,
    featuresByCategory,
    loading,
    error,
    isFeatureEnabled,
    updateFeature,
    refetch: fetchSettings
  };
};

// Convenience hook for checking AI feature settings specifically
export const useAIFeatures = (organizationId: string | null) => {
  const { features, loading, error, isFeatureEnabled, updateFeature, refetch } = useOrganizationSettings(organizationId);

  const aiFeatures = useMemo(() => 
    features.filter(f => f.feature_key.startsWith('ai_summary_')),
    [features]
  );

  return {
    aiFeatures,
    loading,
    error,
    isShortSummaryEnabled: isFeatureEnabled('ai_summary_short'),
    isLongSummaryEnabled: isFeatureEnabled('ai_summary_long'),
    isProPositionEnabled: isFeatureEnabled('ai_summary_pro'),
    isConPositionEnabled: isFeatureEnabled('ai_summary_con'),
    updateFeature,
    refetch
  };
};
